import { useState, useEffect } from 'react';
import type { ViewType } from 'calendarkit-pro';

/**
 * Retrieves the saved calendar view mode
 * @param defaultView The default view mode to return if none is saved
 * @returns The saved view mode or the default
 */
const getSavedCalendarView = (defaultView: ViewType = "week"): ViewType => {
  const savedView = localStorage.getItem('calendar-view-mode');
  return (savedView as ViewType) || defaultView;
};

/**
 * Saves the calendar view mode
 * @param view The view mode to save
 */
const saveCalendarView = (view: ViewType): void => {
  localStorage.setItem('calendar-view-mode', view);
};

/**
 * Retrieves the saved sidebar expanded state
 * @param defaultExpanded The default expanded state
 * @returns The saved sidebar state or the default
 */
const getSavedSidebarExpanded = (defaultExpanded: boolean = false): boolean => {
  const saved = localStorage.getItem('calendar-sidebar-expanded-v2');
  if (saved === null) return defaultExpanded;
  return saved === 'true';
};

/**
 * Saves the sidebar expanded state
 * @param expanded Whether the sidebar is expanded
 */
const saveSidebarExpanded = (expanded: boolean): void => {
  localStorage.setItem('calendar-sidebar-expanded-v2', expanded.toString());
};

/**
 * Custom hook to manage calendar view persistence
 * @param defaultView The default view mode
 * @returns The current view mode and a setter function
 */
export const usePersistedCalendarView = (defaultView: ViewType = "week"): [ViewType, (view: ViewType) => void] => {
  const [view, setView] = useState<ViewType>(() => getSavedCalendarView(defaultView));
  
  useEffect(() => {
    saveCalendarView(view);
  }, [view]);
  
  return [view, setView];
};

/**
 * Custom hook to manage sidebar expanded state persistence
 * @param defaultExpanded The default expanded state
 * @returns The current expanded state and a setter function
 */
export const usePersistedSidebarExpanded = (defaultExpanded: boolean = true): [boolean, (expanded: boolean) => void] => {
  const [expanded, setExpanded] = useState<boolean>(() => getSavedSidebarExpanded(defaultExpanded));
  
  useEffect(() => {
    saveSidebarExpanded(expanded);
  }, [expanded]);
  
  return [expanded, setExpanded];
};
